import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  DollarSign,
  Users,
  Utensils,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_tip_reports,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

export default function TipReport() {
  const { toast } = useToast();

  // Date states
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(new Date());
  const [filterOption, setFilterOption] = useState("");

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [employeeData, setEmployeeData] = useState([]);
  const [tipData, setTipData] = useState([]);

  // Stats
  const [stats, setStats] = useState({
    totalTip: 0,
    totalEmployees: 0,
    totalTables: 0,
    averageTip: 0,
  });

  // Fetch data
  const fetchTipData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      // Handle date filtering
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay())
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startDate || !endDate) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", start_date.toISOString().split("T")[0]);
      fd.append("end_date", end_date.toISOString().split("T")[0]);
      fd.append("flag", flag);
      fd.append("call_id", call_id);

      try {
        const response = await server_post_data(get_tip_reports, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          // console.log("Resp", response.data.message)
          const empData = response.data.message.staff_tip_data || [];
          const tableData =
            empData.length > 0
              ? response.data.message.table_tip_data || []
              : [];

          setEmployeeData(empData);
          setTipData(tableData);

          // Calculate stats
          const totalTip = empData.reduce(
            (sum, item) => sum + (item.total_tip || 0),
            0
          );
          const averageTip = empData.length > 0 ? totalTip / empData.length : 0;

          setStats({
            totalTip: totalTip,
            totalEmployees: empData.length,
            totalTables: tableData.length,
            averageTip: averageTip,
          });
        }
      } catch (error) {
        console.error("Error fetching tip data:", error);
        toast({
          title: "Error",
          description: "Failed to load tip data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startDate, endDate, toast]
  );

  // Handle search
  const handleSearch = useCallback(() => {
    fetchTipData(startDate, endDate, "2", "0");
  }, [startDate, endDate, fetchTipData]);

  // Export to Excel
  const exportToExcel = useCallback(() => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const ws = XLSX.utils.json_to_sheet(tipData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, "tip_report.xlsx");

    toast({
      title: "Success",
      description: "Report exported successfully",
    });
  }, [tipData, toast]);

  // Initial load
  useEffect(() => {
    fetchTipData(startDate, endDate, "1", "0");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Tip Report"
        subtitle="Track and analyze tip distribution across employees and tables"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalTip.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Tips</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalEmployees}
                </p>
                <p className="text-sm text-muted-foreground">Employees</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalTables}
                </p>
                <p className="text-sm text-muted-foreground">Tables</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.averageTip.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Avg Tip/Employee
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? (
                          format(startDate, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* End Date */}
              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? (
                          format(endDate, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Action Buttons */}
              <div
                className={cn(
                  "flex items-end gap-2",
                  filterOption !== "custom" && "md:col-span-3"
                )}
              >
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={exportToExcel}
                  className="gap-2"
                  disabled={tipData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Employee Wise Tip */}
          <Card>
            <CardHeader className="bg-muted/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5" />
                Employee Wise Tip
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">
                        Employee
                      </TableHead>
                      <TableHead className="text-muted-foreground text-right">
                        Amount (₹)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showLoaderAdmin ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          className="text-center text-muted-foreground"
                        >
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : employeeData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          className="text-center text-muted-foreground"
                        >
                          No data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      employeeData.map((item, index) => (
                        <TableRow
                          key={`employee-${index}`}
                          className="border-border"
                        >
                          <TableCell className="font-medium">
                            {item?.assigned_staff_name || "NA"}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-success">
                            ₹{item.total_tip.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Table Wise Tip */}
          <Card>
            <CardHeader className="bg-muted/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Utensils className="w-5 h-5" />
                Table Wise Tip
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">
                        Table
                      </TableHead>
                      <TableHead className="text-muted-foreground text-right">
                        Amount (₹)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showLoaderAdmin ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          className="text-center text-muted-foreground"
                        >
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : tipData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          className="text-center text-muted-foreground"
                        >
                          No data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      tipData.map((row, index) => (
                        <TableRow key={index} className="border-border">
                          <TableCell className="font-medium">
                            {row.table_name}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-success">
                            ₹{row.total_tip.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
